/* =============================================================
   useAnalytics.js — Real analytics data from Supabase
   -------------------------------------------------------------
   Fetches all students (with partner info) and partner count in
   two parallel queries, then aggregates into the shapes that
   Analytics.jsx expects:

     semesterRecords — [{ year, sem, sent, received }, ...]
       Groups students by calendar year + semester derived from
       created_at (Jan–Jun = Spring, Jul–Dec = Fall).

     partnerStats — [{ id, name, country, type, sent, received, balance }, ...]
       Aggregates how many outgoing/incoming students are linked
       to each partner university.

     partnerCount — total number of partner universities

   TAB-FREEZE RESILIENCE:
     AbortController + 8-second timeout on every fetch.
     visibilitychange listener refetches when the tab regains focus.

   ✓ DONE (backend integration):
   - Replaces the hardcoded semesterRecords / partners / topSenders /
     toRebalance arrays that were in Analytics.jsx
   - Two parallel Supabase queries (students + partner count)
   - Semester derived from created_at (no DB schema change needed)
   ============================================================= */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase }  from '../lib/supabase';
import { useAuth }   from '../context/AuthContext';

const FETCH_TIMEOUT_MS = 8000;


export function useAnalytics() {
  const { user } = useAuth();

  const [semesterRecords, setSemesterRecords] = useState([]);
  const [semesters,       setSemesters]       = useState([]);
  const [partnerStats,    setPartnerStats]    = useState([]);
  const [partnerCount,    setPartnerCount]    = useState(0);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);

  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!user?.id) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setError(null);

    try {
      const [studentsRes, countRes, semestersRes] = await Promise.all([
        supabase
          .from('students')
          // include archived students — analytics covers all historical data
          .select('id, type, semester_id, partner_id, partner_universities(id, name, country, type)')
          .eq('university_id', user.universityId)
          .abortSignal(signal),
        supabase
          .from('partner_universities')
          .select('*', { count: 'exact', head: true })
          .eq('university_id', user.universityId)
          .abortSignal(signal),
        supabase
          .from('semesters')
          .select('id, name')
          .eq('university_id', user.universityId)
          .order('created_at', { ascending: true })
          .abortSignal(signal),
      ]);

      if (signal.aborted) return;

      const students = studentsRes.data ?? [];
      setPartnerCount(countRes.count ?? 0);

      /* ── Semesters (named terms) ───────────────────────── */
      const semesterList = semestersRes.data ?? [];
      setSemesters(semesterList);

      // Map id → name for fast lookup
      const semNameMap = Object.fromEntries(semesterList.map(s => [s.id, s.name]));
      // Preserve creation order for sorting
      const semOrder   = Object.fromEntries(semesterList.map((s, i) => [s.id, i]));

      /* ── Semester records ──────────────────────────────── */
      const recMap = {};
      students.forEach(s => {
        const termId   = s.semester_id ?? null;
        const termName = termId ? (semNameMap[termId] ?? 'Unknown') : 'Unassigned';
        const key      = termId ?? '__unassigned__';

        if (!recMap[key]) recMap[key] = { termId, termName, sent: 0, received: 0 };
        if (s.type === 'outgoing') recMap[key].sent++;
        if (s.type === 'incoming') recMap[key].received++;
      });

      const records = Object.values(recMap).sort((a, b) => {
        if (a.termId === null) return 1;   // Unassigned always last
        if (b.termId === null) return -1;
        return (semOrder[a.termId] ?? 999) - (semOrder[b.termId] ?? 999);
      });
      setSemesterRecords(records);


      /* ── Partner stats ─────────────────────────────────── */
      const pMap = {};
      students.forEach(s => {
        if (!s.partner_id || !s.partner_universities) return;
        const p = s.partner_universities;

        if (!pMap[s.partner_id]) {
          const typeLabel =
            p.type === 'ERASMUS'       ? 'Erasmus'       :
            p.type === 'DOUBLE DEGREE' ? 'Double Degree' : 'Bilateral';

          pMap[s.partner_id] = {
            id:       s.partner_id,
            name:     p.name,
            country:  p.country,
            type:     typeLabel,
            sent:     0,
            received: 0,
          };
        }

        if (s.type === 'outgoing') pMap[s.partner_id].sent++;
        if (s.type === 'incoming') pMap[s.partner_id].received++;
      });

      const stats = Object.values(pMap).map(p => ({
        ...p,
        balance: p.sent - p.received,
      }));
      setPartnerStats(stats);

    } catch (err) {
      if (signal.aborted) return;
      setError(err?.message ?? 'Failed to load analytics');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    load();
  }, [load]);

  /* ── Refetch when the user switches back to this tab ─────────── */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [load]);

  /* ── Real-time subscription ──────────────────────────────────── */
  // Re-aggregates analytics whenever a student is added, updated,
  // or removed — keeps charts in sync for all users in real time.
  useEffect(() => {
    if (!user?.universityId) return;

    const channel = supabase
      .channel(`analytics_${user.universityId}`)
      .on(
        'postgres_changes',
        {
          event:  '*',
          schema: 'public',
          table:  'students',
          filter: `university_id=eq.${user.universityId}`,
        },
        () => load()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.universityId, load]);

  /* ── Abort on unmount ────────────────────────────────────────── */
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);


  return { semesterRecords, semesters, partnerStats, partnerCount, loading, error };
}
